import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import { create } from 'zustand'

import type { ConversationTarget, TimelineItem } from './conversation'

interface MessageHighlightState {
  /** Conversation the pending highlight belongs to. */
  conversationId: string | null
  /** Timeline item id to flash once its thread opens. */
  messageId: string | null
  /** Queue a message to flash when its conversation opens (e.g. a notification
   *  click pointing at the SMS that triggered it). */
  highlightMessage: (conversationId: string, messageId: string) => void
  /** Drop the pending highlight once it's been consumed. */
  clear: () => void
}

export const useMessageHighlightStore = create<MessageHighlightState>(
  (set) => ({
    conversationId: null,
    messageId: null,
    highlightMessage: (conversationId, messageId) =>
      set({ conversationId, messageId }),
    clear: () => set({ conversationId: null, messageId: null }),
  }),
)

/** Imperative setter for callers outside the conversation tree (e.g. the
 *  notifications widget reacting to a click). */
export function highlightMessage(
  conversationId: string,
  messageId: string,
): void {
  useMessageHighlightStore
    .getState()
    .highlightMessage(conversationId, messageId)
}

/** How long a jumped-to message keeps its flash before settling. */
const FLASH_DURATION_MS = 2200

/**
 * Consumes a pending message highlight for the open thread: once the target
 * message is in the loaded timeline it scrolls the bubble into view and returns
 * its id so the bubble can flash. The flash clears itself after a short beat.
 */
export function useMessageHighlight(
  scrollRef: RefObject<HTMLElement | null>,
  target: ConversationTarget | null,
  timeline: ReadonlyArray<TimelineItem>,
  isLoading: boolean,
  /** Jump without animating. A notification lands on a message that's usually
   *  already close by, so it glides; an anchored open starts at the top of a
   *  freshly loaded page, and a long smooth scroll there fires scroll events all
   *  the way down — which the load-older/newer handlers act on mid-flight. */
  instant = false,
): string | null {
  const conversationId = useMessageHighlightStore((s) => s.conversationId)
  const messageId = useMessageHighlightStore((s) => s.messageId)
  const clear = useMessageHighlightStore((s) => s.clear)
  const [flashId, setFlashId] = useState<string | null>(null)

  // Decide to flash once the loaded thread holds the target message. Driven by
  // state (not a rAF) so a re-render from the live-updating timeline can't cancel
  // it mid-flight; consuming the store immediately stops it re-firing.
  useEffect(() => {
    if (isLoading || !messageId) return
    if (conversationId !== target?.conversationId) return
    const present = timeline.some(
      (item) => item.kind !== 'date' && item.id === messageId,
    )
    if (!present) return
    setFlashId(messageId)
    clear()
  }, [
    isLoading,
    conversationId,
    messageId,
    target?.conversationId,
    timeline,
    clear,
  ])

  // After the flash class is on the bubble, jump it into view and end the flash.
  useEffect(() => {
    if (!flashId) return
    scrollRef.current
      ?.querySelector(`[data-message-id="${CSS.escape(flashId)}"]`)
      ?.scrollIntoView({
        block: 'center',
        behavior: instant ? 'auto' : 'smooth',
      })
    const timer = setTimeout(() => setFlashId(null), FLASH_DURATION_MS)
    return () => clearTimeout(timer)
  }, [flashId, scrollRef, instant])

  return flashId
}
